import React, { ReactNode, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Toolbar,
  AppBar,
  Menu,
  MenuItem,
  Drawer,
  IconButton,
  Tooltip,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FolderIcon from "@mui/icons-material/Folder";
import CurrencyYenIcon from "@mui/icons-material/CurrencyYen";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ConstructionIcon from "@mui/icons-material/Construction";
import PhoneLockedIcon from "@mui/icons-material/PhoneLocked";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import WebIcon from "@mui/icons-material/Web";
import TuneIcon from "@mui/icons-material/Tune";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BarChartIcon from "@mui/icons-material/BarChart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WorkIcon from "@mui/icons-material/Work";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

// jotai
import { useAtom, useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";
import { useWindowSize } from "../lib/useWindowSize";
import { isProductionEnv } from "../config/const";

interface TableTopBarProps {
  serch: boolean;
  serchOpen: boolean;
  setSerchOpen: (serchOpen: boolean) => void;
  addDataNames: string[];
  addDataName: string;
  setAddDataName: (name: string) => void;
  addCSVDataNames: string[];
  addCSVDataName: string;
  setAddCSVDataName: (name: string) => void;
  getXLSXDataNames: string[];
  getXLSXDataName: string;
  setGetXLSXDataName: (name: string) => void;
  editDatasNames: string[];
  editDatasName: string;
  setEditDatasName: (name: string) => void;
  editIDs: number[];
  columnSettingOpen: boolean;
  openColumnSettingWindow: () => void;
  // 一括設定ドロップダウン
  bulkSettingNames?: string[];
  bulkSettingName?: string;
  setBulkSettingName?: (name: string) => void;
  // AIチャット
  aiChatOpen?: boolean;
  setAiChatOpen?: (open: boolean) => void;
  // E2Eテスト用 data-testid
  addDataTestIds?: string[];
  bulkSettingTestIds?: string[];
}

function TableTopBar(props: TableTopBarProps) {
  const {
    serch,
    serchOpen,
    setSerchOpen,
    addDataNames,
    addDataName,
    setAddDataName,
    addCSVDataNames,
    addCSVDataName,
    setAddCSVDataName,
    getXLSXDataNames,
    getXLSXDataName,
    setGetXLSXDataName,
    editDatasNames,
    editDatasName,
    setEditDatasName,
    editIDs,
    columnSettingOpen,
    openColumnSettingWindow,
    bulkSettingNames = [],
    bulkSettingName,
    setBulkSettingName,
    aiChatOpen,
    setAiChatOpen,
    addDataTestIds = [],
    bulkSettingTestIds = [],
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const [pathname, setPathname] = useAtom(AppAtoms.pathname);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    analytics: true,
    operations: true,
    billing: false,
    master: false,
    forms: false,
    account: false,
    settings: false,
  });
  const handleToggleGroup = (group: string) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const [anchorElMenuAddData, setAnchorElMenuAddData] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuAddData = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuAddData(event.currentTarget);
  };
  const handleCloseMenuAddData = () => {
    setAnchorElMenuAddData(null);
  };
  const [anchorElMenuEditDatas, setAnchorElMenuEditDatas] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuEditDatas = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuEditDatas(event.currentTarget);
  };
  const handleCloseMenuEditDatas = () => {
    setAnchorElMenuEditDatas(null);
  };
  const [anchorElMenuAddCSVData, setAnchorElMenuAddCSVData] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuAddCSVData = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuAddCSVData(event.currentTarget);
  };
  const handleCloseMenuAddCSVData = () => {
    setAnchorElMenuAddCSVData(null);
  };
  const [anchorElMenuGetXLSXData, setAnchorElMenuGetXLSXData] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuGetXLSXData = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuGetXLSXData(event.currentTarget);
  };
  const handleCloseMenuGetXLSXData = () => {
    setAnchorElMenuGetXLSXData(null);
  };
  const [anchorElMenuBulkSetting, setAnchorElMenuBulkSetting] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuBulkSetting = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuBulkSetting(event.currentTarget);
  };
  const handleCloseMenuBulkSetting = () => {
    setAnchorElMenuBulkSetting(null);
  };

  interface MenuListProps {
    itemPath: string;
    primary: string;
    iconComponent: ReactNode;
  }
  function MenuList(menuListProps: MenuListProps) {
    const { itemPath, primary, iconComponent } = menuListProps;
    return (
      <Link
        style={{ color: "inherit", textDecoration: "none" }}
        to={itemPath}
        onClick={() => setPathname(itemPath)}
      >
        <ListItemButton
          onClick={() => setDrawerOpen(false)}
          sx={{
            height: "42px",
            pl: 4,
            pr: 2,
            borderLeft:
              pathname === itemPath
                ? "3px solid #1976d2"
                : "3px solid transparent",
            backgroundColor:
              pathname === itemPath
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
            "&:hover": {
              backgroundColor:
                pathname === itemPath
                  ? "rgba(25, 118, 210, 0.12)"
                  : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <Box className="header-manu" sx={{ flex: 1 }}>
            {primary}
          </Box>
          {iconComponent}
        </ListItemButton>
      </Link>
    );
  }
  const { windowWidth, windowHeight } = useWindowSize();
  const [logoMarginTop, setLogoMarginTop] = useState("-24px");

  useEffect(() => {
    let logoMarginTop = "-24px";
    if (windowWidth < 600) {
      logoMarginTop = "-15px";
    }
    setLogoMarginTop(logoMarginTop);
  }, [windowWidth]);

  const groupHeaderSx = {
    py: 0.35,
    px: 2,
    backgroundColor: "rgba(25, 118, 210, 0.04)",
    mt: 0.5,
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
    },
  };
  const groupLabelProps = {
    fontSize: "1.1rem",
    fontWeight: "400",
    margin: "4px 0px",
  };

  return (
    <AppBar
      position={isSmartPhone ? "fixed" : "static"}
      style={{
        boxShadow: "none",
        height: "40px",
        borderBottom: "silver 0.2px solid",
        backgroundColor: "white",
        width: isSmartPhone
          ? "100%"
          : serchOpen || aiChatOpen
            ? "calc(100% + 331px)"
            : "calc(100% + 31px)",
        top: isSmartPhone ? "40px" : undefined,
      }}
    >
      <Toolbar>
        <Tooltip title="メニュー">
          <IconButton
            size="small"
            aria-label="メニューを開く"
            onClick={() => setDrawerOpen(true)}
            color="default"
            sx={{
              marginTop: logoMarginTop,
            }}
          >
            <MenuIcon
              fontSize="large"
              sx={{
                color: "rgb(70 70 70)",
                width: "20px",
                height: "20px",
              }}
            />
          </IconButton>
        </Tooltip>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          variant="temporary"
          sx={{
            "& .MuiDrawer-paper": {
              width: 280,
              boxSizing: "border-box",
            },
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {/* グループ1: 分析・実績 */}
              <ListItemButton
                onClick={() => handleToggleGroup("analytics")}
                sx={groupHeaderSx}
              >
                <BarChartIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "#1976d2" }}
                />
                <ListItemText
                  primary="分析・実績"
                  primaryTypographyProps={groupLabelProps}
                />
                {openGroups.analytics ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </ListItemButton>
              <Collapse in={openGroups.analytics} timeout="auto" unmountOnExit>
                <MenuList
                  itemPath="/dashboard"
                  primary="ダッシュボード"
                  iconComponent={
                    <DashboardIcon
                      fontSize="medium"
                      sx={{
                        color: "#1976d2",
                        position: "absolute",
                        right: "15px",
                      }}
                    />
                  }
                />
                {!isProductionEnv && (
                  <MenuList
                    itemPath="/data-analysis"
                    primary="AIデータ分析"
                    iconComponent={
                      <SmartToyIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                )}
                {loginUser?.db_authority !== "営業アカウント" &&
                loginUser?.db_authority !== "トスアップアカウント" ? (
                  <MenuList
                    itemPath="/runking"
                    primary="ランキング"
                    iconComponent={
                      <EmojiEventsIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                ) : null}
                {loginUser?.authority === "マスターアカウント" ||
                loginUser?.authority === "スタッフアカウント" ? (
                  <MenuList
                    itemPath="/product_partner_result"
                    primary="推進実績"
                    iconComponent={
                      <AssessmentIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                ) : null}
              </Collapse>

              {/* グループ2: 業務 */}
              <ListItemButton
                onClick={() => handleToggleGroup("operations")}
                sx={groupHeaderSx}
              >
                <WorkIcon fontSize="small" sx={{ mr: 1, color: "#1976d2" }} />
                <ListItemText
                  primary="業務"
                  primaryTypographyProps={groupLabelProps}
                />
                {openGroups.operations ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </ListItemButton>
              <Collapse in={openGroups.operations} timeout="auto" unmountOnExit>
                <MenuList
                  itemPath="/toss_up"
                  primary="トスアップ管理"
                  iconComponent={
                    <TrendingUpIcon
                      fontSize="medium"
                      sx={{
                        color: "#1976d2",
                        position: "absolute",
                        right: "15px",
                      }}
                    />
                  }
                />
                {loginUser?.db_authority !== "トスアップアカウント" ? (
                  <MenuList
                    itemPath="/contract"
                    primary="エントリー管理"
                    iconComponent={
                      <PersonAddIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                ) : null}
                {loginUser?.db_authority !== "トスアップアカウント" ? (
                  <MenuList
                    itemPath="/follow_call"
                    primary="フォローコール一覧"
                    iconComponent={
                      <LocalPhoneIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                ) : null}
              </Collapse>

              {/* グループ3: 明細 */}
              {(loginUser?.authority === "マスターアカウント" ||
                loginUser?.authority === "パートナー") && (
                <>
                  <ListItemButton
                    onClick={() => handleToggleGroup("billing")}
                    sx={groupHeaderSx}
                  >
                    <CurrencyYenIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "#1976d2" }}
                    />
                    <ListItemText
                      primary="明細"
                      primaryTypographyProps={groupLabelProps}
                    />
                    {openGroups.billing ? (
                      <ExpandLessIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={openGroups.billing}
                    timeout="auto"
                    unmountOnExit
                  >
                    {loginUser?.authority === "マスターアカウント" ? (
                      <MenuList
                        itemPath="/payment_admin"
                        primary="明細管理"
                        iconComponent={
                          <CurrencyYenIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                    <MenuList
                      itemPath="/payment"
                      primary={
                        loginUser?.authority === "マスターアカウント"
                          ? "確定済 - 明細一覧"
                          : "明細一覧"
                      }
                      iconComponent={
                        <CurrencyYenIcon
                          fontSize="medium"
                          sx={{
                            color: "#1976d2",
                            position: "absolute",
                            right: "15px",
                          }}
                        />
                      }
                    />
                  </Collapse>
                </>
              )}

              {/* グループ4: マスタ管理 */}
              {(loginUser?.authority === "マスターアカウント" ||
                loginUser?.authority === "スタッフアカウント") && (
                <>
                  <ListItemButton
                    onClick={() => handleToggleGroup("master")}
                    sx={groupHeaderSx}
                  >
                    <FolderIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "#1976d2" }}
                    />
                    <ListItemText
                      primary="マスタ管理"
                      primaryTypographyProps={groupLabelProps}
                    />
                    {openGroups.master ? (
                      <ExpandLessIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    )}
                  </ListItemButton>
                  <Collapse in={openGroups.master} timeout="auto" unmountOnExit>
                    <MenuList
                      itemPath="/product"
                      primary="商材管理"
                      iconComponent={
                        <FolderIcon
                          fontSize="medium"
                          sx={{
                            color: "#1976d2",
                            position: "absolute",
                            right: "15px",
                          }}
                        />
                      }
                    />
                    <MenuList
                      itemPath="/service"
                      primary="サービスプラン管理"
                      iconComponent={
                        <FolderIcon
                          fontSize="medium"
                          sx={{
                            color: "#1976d2",
                            position: "absolute",
                            right: "15px",
                          }}
                        />
                      }
                    />
                    <MenuList
                      itemPath="/incentive"
                      primary="インセンティブ管理"
                      iconComponent={
                        <FolderIcon
                          fontSize="medium"
                          sx={{
                            color: "#1976d2",
                            position: "absolute",
                            right: "15px",
                          }}
                        />
                      }
                    />
                  </Collapse>
                </>
              )}

              {/* グループ5: フォーム・サイト */}
              {(loginUser?.authority === "マスターアカウント" ||
                loginUser?.authority === "スタッフアカウント" ||
                loginUser?.db_authority !== "トスアップアカウント") && (
                <>
                  <ListItemButton
                    onClick={() => handleToggleGroup("forms")}
                    sx={groupHeaderSx}
                  >
                    <EditNoteIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "#1976d2" }}
                    />
                    <ListItemText
                      primary="フォーム・サイト"
                      primaryTypographyProps={groupLabelProps}
                    />
                    {openGroups.forms ? (
                      <ExpandLessIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    )}
                  </ListItemButton>
                  <Collapse in={openGroups.forms} timeout="auto" unmountOnExit>
                    {loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ? (
                      <MenuList
                        itemPath="/toss_up_form"
                        primary="トスアップフォーム管理"
                        iconComponent={
                          <ConstructionIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                    {loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ? (
                      <MenuList
                        itemPath="/entry_form"
                        primary="エントリーフォーム管理"
                        iconComponent={
                          <ConstructionIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                    {loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ? (
                      <MenuList
                        itemPath="/outside_entry_site"
                        primary="外部ETサイト管理"
                        iconComponent={
                          <WebIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : loginUser?.db_authority !== "トスアップアカウント" ? (
                      <MenuList
                        itemPath="/outside_entry_site"
                        primary="外部ETサイト"
                        iconComponent={
                          <WebIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                    {loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ? (
                      <MenuList
                        itemPath="/outside_entry_form"
                        primary="外部ETフォーム管理"
                        iconComponent={
                          <WebIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                  </Collapse>
                </>
              )}
            </Box>
            <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
              {/* グループ6: アカウント */}
              {(loginUser?.authority === "マスターアカウント" ||
                loginUser?.authority === "スタッフアカウント" ||
                loginUser?.authority === "パートナー") && (
                <>
                  <ListItemButton
                    onClick={() => handleToggleGroup("account")}
                    sx={groupHeaderSx}
                  >
                    <ManageAccountsIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "#1976d2" }}
                    />
                    <ListItemText
                      primary="アカウント"
                      primaryTypographyProps={groupLabelProps}
                    />
                    {openGroups.account ? (
                      <ExpandLessIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={openGroups.account}
                    timeout="auto"
                    unmountOnExit
                  >
                    {loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ? (
                      <MenuList
                        itemPath="/partner"
                        primary="パートナー管理"
                        iconComponent={
                          <ManageAccountsIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                    {loginUser?.authority === "パートナー" ? (
                      <MenuList
                        itemPath="/config"
                        primary="企業・グループ管理"
                        iconComponent={
                          <ManageAccountsIcon
                            fontSize="medium"
                            sx={{
                              color: "#1976d2",
                              position: "absolute",
                              right: "15px",
                            }}
                          />
                        }
                      />
                    ) : null}
                    <MenuList
                      itemPath="/account"
                      primary="アカウント管理"
                      iconComponent={
                        <ManageAccountsIcon
                          fontSize="medium"
                          sx={{
                            color: "#1976d2",
                            position: "absolute",
                            right: "15px",
                          }}
                        />
                      }
                    />
                  </Collapse>
                </>
              )}

              {/* グループ7: 設定 */}
              <ListItemButton
                onClick={() => handleToggleGroup("settings")}
                sx={groupHeaderSx}
              >
                <SettingsIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "#1976d2" }}
                />
                <ListItemText
                  primary="設定"
                  primaryTypographyProps={groupLabelProps}
                />
                {openGroups.settings ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </ListItemButton>
              <Collapse in={openGroups.settings} timeout="auto" unmountOnExit>
                {loginUser?.authority === "マスターアカウント" ||
                loginUser?.authority === "スタッフアカウント" ? (
                  <MenuList
                    itemPath="/follow_call_config"
                    primary="フォローコール設定"
                    iconComponent={
                      <PhoneLockedIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                ) : null}
                {loginUser?.db_authority !== "営業アカウント" &&
                loginUser?.db_authority !== "トスアップアカウント" ? (
                  <MenuList
                    itemPath="/column_config"
                    primary="システム項目設定"
                    iconComponent={
                      <SettingsIcon
                        fontSize="medium"
                        sx={{
                          color: "#1976d2",
                          position: "absolute",
                          right: "15px",
                        }}
                      />
                    }
                  />
                ) : null}
                <MenuList
                  itemPath="/inquiry"
                  primary="システム問い合わせ"
                  iconComponent={
                    <HelpOutlineIcon
                      fontSize="medium"
                      sx={{
                        color: "#1976d2",
                        position: "absolute",
                        right: "15px",
                      }}
                    />
                  }
                />
              </Collapse>
            </Box>
          </Box>
        </Drawer>
        <Box
          sx={{
            marginLeft: "auto",
          }}
        />
        {addDataNames.length > 0 ? (
          <Tooltip
            title={addDataNames.length === 1 ? addDataNames[0] : "新規登録"}
          >
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              data-testid={addDataNames.length === 1 ? addDataTestIds[0] : undefined}
              onClick={(e) => {
                if (addDataNames.length === 1) {
                  setAddDataName(addDataNames[0]);
                } else {
                  handleOpenMenuAddData(e);
                }
              }}
              color="default"
              sx={{
                marginTop: logoMarginTop,
              }}
            >
              <AddCircleOutlineIcon
                fontSize="large"
                sx={{
                  color: addDataName !== "" ? "primary.dark" : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        <Menu
          id="menu-appbar"
          anchorEl={anchorElMenuAddData}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElMenuAddData)}
          onClose={handleCloseMenuAddData}
        >
          {addDataNames.map((dataName, i) => {
            return (
              <MenuItem
                data-testid={addDataTestIds[i]}
                onClick={() => {
                  setAddDataName(dataName);
                  handleCloseMenuAddData();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            );
          })}
        </Menu>
        {editDatasNames.length > 0 ? (
          <Tooltip
            title={editDatasNames.length === 1 ? editDatasNames[0] : "新規登録"}
          >
            <IconButton
              disabled={editIDs.length === 0}
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={(e) => {
                if (editDatasNames.length === 1) {
                  setEditDatasName(editDatasNames[0]);
                } else {
                  handleOpenMenuEditDatas(e);
                }
              }}
              color="default"
              sx={{
                marginTop: logoMarginTop,
              }}
            >
              <EditNoteIcon
                fontSize="large"
                sx={{
                  color:
                    editIDs.length === 0
                      ? undefined
                      : editDatasName !== ""
                        ? "primary.dark"
                        : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        <Menu
          id="menu-appbar"
          anchorEl={anchorElMenuEditDatas}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElMenuEditDatas)}
          onClose={handleCloseMenuEditDatas}
        >
          {editDatasNames.map((dataName) => {
            return (
              <MenuItem
                onClick={() => {
                  setEditDatasName(dataName);
                  handleCloseMenuEditDatas();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            );
          })}
        </Menu>

        {addCSVDataNames.length > 0 && !isSmartPhone ? (
          <Tooltip
            title={
              addCSVDataNames.length === 1 ? addCSVDataNames[0] : "CSV取込"
            }
          >
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={(e) => {
                if (addCSVDataNames.length === 1) {
                  setAddCSVDataName(addCSVDataNames[0]);
                } else {
                  handleOpenMenuAddCSVData(e);
                }
              }}
              color="default"
              sx={{
                marginTop: logoMarginTop,
              }}
            >
              <AttachFileIcon
                fontSize="large"
                sx={{
                  color:
                    addCSVDataName !== "" ? "primary.dark" : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        <Menu
          id="menu-appbar"
          anchorEl={anchorElMenuAddCSVData}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElMenuAddCSVData)}
          onClose={handleCloseMenuAddCSVData}
        >
          {addCSVDataNames.map((dataName) => {
            return (
              <MenuItem
                onClick={() => {
                  setAddCSVDataName(dataName);
                  handleCloseMenuAddCSVData();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            );
          })}
        </Menu>

        {getXLSXDataNames.length > 0 && !isSmartPhone ? (
          <Tooltip
            title={
              getXLSXDataNames.length === 1
                ? getXLSXDataNames[0]
                : "ファイル出力"
            }
          >
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={(e) => {
                if (getXLSXDataNames.length === 1) {
                  setGetXLSXDataName(getXLSXDataNames[0]);
                } else {
                  handleOpenMenuGetXLSXData(e);
                }
              }}
              color="default"
              sx={{
                marginTop: logoMarginTop,
              }}
            >
              <FileDownloadIcon
                fontSize="large"
                sx={{
                  color:
                    getXLSXDataName !== "" ? "primary.dark" : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        <Menu
          id="menu-appbar"
          anchorEl={anchorElMenuGetXLSXData}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElMenuGetXLSXData)}
          onClose={handleCloseMenuGetXLSXData}
        >
          {getXLSXDataNames.map((dataName) => {
            return (
              <MenuItem
                onClick={() => {
                  setGetXLSXDataName(dataName);
                  handleCloseMenuGetXLSXData();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            );
          })}
        </Menu>

        {/* 一括設定ドロップダウン */}
        {bulkSettingNames.length > 0 && !isSmartPhone ? (
          <>
            <Tooltip title="一括設定">
              <IconButton
                size="small"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenMenuBulkSetting}
                color="default"
                sx={{ marginTop: logoMarginTop }}
              >
                <TuneIcon
                  fontSize="large"
                  sx={{
                    color: bulkSettingName ? "primary.dark" : "rgb(70 70 70)",
                    width: "20px",
                    height: "20px",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar-bulk"
              anchorEl={anchorElMenuBulkSetting}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorElMenuBulkSetting)}
              onClose={handleCloseMenuBulkSetting}
            >
              {bulkSettingNames.map((name, i) => (
                <MenuItem
                  key={name}
                  data-testid={bulkSettingTestIds[i]}
                  onClick={() => {
                    if (setBulkSettingName) setBulkSettingName(name);
                    handleCloseMenuBulkSetting();
                  }}
                >
                  <Box className="header-manu">{name}</Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : null}

        {/* AIサポートチャット */}
        {setAiChatOpen !== undefined ? (
          <Tooltip title="AIサポート">
            <IconButton
              size="small"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={() => setAiChatOpen && setAiChatOpen(!aiChatOpen)}
              color="default"
              sx={{ marginTop: logoMarginTop }}
            >
              <SmartToyIcon
                fontSize="large"
                sx={{
                  color: aiChatOpen ? "primary.dark" : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}

        {serch ? (
          <Tooltip title="検索">
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={() => setSerchOpen(!serchOpen)}
              color="default"
              sx={{
                marginTop: logoMarginTop,
              }}
            >
              <SearchIcon
                fontSize="large"
                sx={{
                  color: serchOpen ? "primary.dark" : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        {pathname === "/toss_up" ||
        pathname === "/contract" ||
        pathname === "/runking" ? (
          <Tooltip title="画面設定">
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={openColumnSettingWindow}
              color="default"
              sx={{
                marginTop: logoMarginTop,
              }}
            >
              <SettingsIcon
                fontSize="large"
                sx={{
                  color: columnSettingOpen ? "primary.dark" : "rgb(70 70 70)",
                  width: "20px",
                  height: "20px",
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}

export default TableTopBar;
